import { createSlice } from "@reduxjs/toolkit";
import { ApiState } from "@baseTypes/baseType";
import WeatherService from "@api/dashboards/WeatherService";
import Logger from "@utils/logger";
import { applyExtraReducer, WeatherData } from "../../../weather";
import { array } from "fp-ts";
import { createThunk } from "../../crops/selector";
import { dashboardURL } from "@utils/utils";
import { IWorkDetailRequestBody } from "@baseTypes/service/dashboard/IWorksDashboardService";
import { Methods } from "@api/dashboards";

export interface WorkDetail {
    date: number
    cultivatedArea: number
}

export type PlanFactDetalization = {
    plan: WorkDetail[]
    fact: WorkDetail[]
}

export type WorkDetalizationItem = {
    cumulative: PlanFactDetalization
    differentiated: PlanFactDetalization
    weather?:  WeatherData[]
    workName: string
    geoZoneIds: number[]
}

export interface WorkDetalizationData {
    detailProgressByDays: WorkDetalizationItem
    detailProgressByHours: WorkDetalizationItem
}

export interface DetalizationWorkState extends ApiState {
    data: WorkDetalizationData
}

const initialState: DetalizationWorkState = {
    data: {
        detailProgressByDays: {
            workName: '',
            cumulative: {
                plan: [],
                fact: []
            },
            differentiated: {
                plan: [],
                fact: []
            },
            geoZoneIds: []
        },
        detailProgressByHours: {
            workName: '',
            cumulative: {
                plan: [],
                fact: []
            },
            differentiated: {
                plan: [],
                fact: []
            },
            geoZoneIds: []
        }
    },
    apiError: undefined,
    isApiLoading: false
}
const log = Logger.getLogger("work.detalization")

const assocWeatherWithWorks: (workData: WorkDetalizationData, thunkApi: any) => Promise<WorkDetalizationData> = async (workData: WorkDetalizationData, thunkApi: any): Promise<WorkDetalizationData> => {
    const times: number[] = workData.detailProgressByDays.cumulative.fact.concat(workData.detailProgressByDays.cumulative.plan).map(i => i.date)
    if (array.isNonEmpty(workData.detailProgressByDays.geoZoneIds) && array.isNonEmpty(times)) {
        const min = Math.min(...times)
        const max = Math.max(...times)

        try {
            const weatherResponse = await WeatherService.fetchWeatherByGeozoneIds(workData.detailProgressByDays.geoZoneIds, min, max, thunkApi)
            if (weatherResponse.success && array.isNonEmpty(weatherResponse.data)) {
                log.debug("Got Weather data", weatherResponse.data)
                return { ...workData, detailProgressByDays: {...workData.detailProgressByDays, weather: weatherResponse.data} }
            } else log.debug("Weather data is empty for range", new Date(min), new Date(max))
        } catch (e) {
            log.error("An error occurred while receiving weathers data from API", e)
        }
    } else {
        log.error("Cannot obtain required parameters for weather request.", workData)
    }
    return workData
}

const fetchWorkDetalization = createThunk<WorkDetalizationData, IWorkDetailRequestBody, IWorkDetailRequestBody>()(
    {
        url: dashboardURL('detailsProgress'),
        async transformer(response, thunkApi) {
            const data = await assocWeatherWithWorks(response.data, thunkApi)
            return { ...response, data }
        },
        method: Methods.POST
    },
    (payload, url) => ({
        url,
        data: payload
    })
)

const workalizationSlice = createSlice({
    name: 'workDetalizationSlice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        applyExtraReducer(builder, fetchWorkDetalization)
    }
})

export const actions = { ...workalizationSlice.actions, fetchWorkDetalization }
export default workalizationSlice.reducer